'use client';
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMobile } from '@repo/ui/src/utils/useMediaQuery';

export interface GaugeProps {
  pelletText?: string;
  lowText?: string;
  criticalText?: string;
  normalText?: string;
}

interface Props extends GaugeProps {
  percentage: number;
  className?: string;
}

const CustomGauge = ({
  percentage,
  pelletText = 'Pellets',
  criticalText = 'Critical',
  lowText = 'Low',
  normalText = 'Normal',
  className,
}: Props) => {
  const isMobile = useIsMobile();
  const radius = isMobile ? 50 : 62;
  const strokeWidth = 6;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const circleRef = useRef<SVGCircleElement>(null);

  const getColorBasedOnPercentage = (percentage: number) => {
    return percentage <= 10 ? 'red' : percentage <= 25 ? 'yellow' : 'green';
  };

  const newPelletText = percentage <= 10 ? criticalText : percentage <= 25 ? lowText : normalText;

  useEffect(() => {
    const targetStrokeDashoffset = circumference - (percentage / 100) * circumference * 0.67;

    if (circleRef.current) {
      gsap.fromTo(
        circleRef.current,
        { strokeDashoffset: circumference },
        {
          strokeDashoffset: targetStrokeDashoffset,
          duration: 1,
          ease: 'power1.out',
        },
      );
    }
  }, [circumference, percentage]);

  return (
    <div className={classNames('flex flex-col items-center justify-center', className)}>
      <svg height={radius * 2} width={radius * 2} viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
        <circle
          stroke="#b3b3b1"
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference * 0.67} ${circumference}`}
          style={{ strokeDashoffset: 0, transform: 'rotate(150deg)', transformOrigin: '50% 50%' }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          ref={circleRef}
          stroke={getColorBasedOnPercentage(percentage)}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          style={{
            strokeDashoffset: circumference, // set initial value to 0%
            transform: 'rotate(150deg)',
            transformOrigin: '50% 50%',
          }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <text
          x="50%"
          y="40%"
          dy=".3em"
          textAnchor="middle"
          className="tra-text-web-heading-4 text-neutral-700"
        >
          {`${percentage}%`}
        </text>
        <text
          x="50%"
          y="75%"
          textAnchor="middle"
          className="tra-text-web-sub-2 text-lg text-neutral-700 font-light"
        >
          <tspan x="50%" dy="0em">
            {pelletText}
          </tspan>
          <tspan x="50%" dy="1.2em">
            {newPelletText}
          </tspan>
        </text>
      </svg>
    </div>
  );
};

export default CustomGauge;
